import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { AppShell, PageHeader, Section, EmptyState } from '../../../components/layout';
import { ActivityCard } from '../../../components/ui';

function buildActivityMeta(activity, t) {
  const parts = [];
  if (activity.estimated_minutes) {
    parts.push(`${activity.estimated_minutes} ${t('common.min')}`);
  }
  const qs = activity.question_summary;
  if (qs) {
    const counts = [];
    if (qs.short_answer) counts.push(`${qs.short_answer} ${t('questions.shortAnswer')}`);
    if (qs.multiple_choice) counts.push(`${qs.multiple_choice} ${t('questions.mcq')}`);
    if (qs.essay) counts.push(`${qs.essay} ${t('questions.essay')}`);
    if (counts.length) parts.push(counts.join(', '));
  }
  if (activity.skills && activity.skills.length > 0) {
    parts.push(activity.skills.join(' · '));
  }
  return parts;
}

export default function ActivitiesIndex({ student, activities }) {
  const { t } = useTranslation();
  if (!activities || activities.length === 0) {
    return (
      <AppShell student={student} scrollTitle={t('common.activities')}>
        <PageHeader
          title={t('studentActivities.yourActivities')}
          subtitle={t('studentActivities.subtitle')}
        />
        <EmptyState
          icon="📚"
          title={t('studentActivities.noActivities')}
          description={t('studentActivities.noActivitiesHelp')}
        />
      </AppShell>
    );
  }

  const completedActivities = activities.filter(l => l.status === 'completed');
  const inProgressActivities = activities.filter(l => l.status === 'in_progress');
  const availableActivities = activities.filter(l => l.status === 'available' || l.status === 'locked');

  return (
    <AppShell student={student} scrollTitle={t('common.activities')}>
      <PageHeader
        title={t('studentActivities.yourActivities')}
        subtitle={t('studentActivities.subtitle')}
      />

      {inProgressActivities.length > 0 && (
        <Section title={t('common.inProgress')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {inProgressActivities.map((activity) => (
              <Link key={activity.id} href={`/student/activities/${activity.id}`}>
                <ActivityCard
                  id={activity.id}
                  title={activity.title}
                  subtitle={activity.subtitle}
                  status={activity.status}
                  progress={activity.progress_percent}
                  meta={buildActivityMeta(activity, t)}
                />
              </Link>
            ))}
          </div>
        </Section>
      )}

      {availableActivities.length > 0 && (
        <Section title={t('common.available')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableActivities.map((activity) => (
              <Link key={activity.id} href={`/student/activities/${activity.id}`}>
                <ActivityCard
                  id={activity.id}
                  title={activity.title}
                  subtitle={activity.subtitle}
                  status={activity.status}
                  progress={activity.progress_percent}
                  meta={buildActivityMeta(activity, t)}
                />
              </Link>
            ))}
          </div>
        </Section>
      )}

      {completedActivities.length > 0 && (
        <Section title={t('common.completed')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {completedActivities.map((activity) => (
              <Link key={activity.id} href={`/student/activities/${activity.id}`}>
                <ActivityCard
                  id={activity.id}
                  title={activity.title}
                  subtitle={activity.subtitle}
                  status={activity.status}
                  progress={activity.progress_percent}
                  meta={buildActivityMeta(activity, t)}
                />
              </Link>
            ))}
          </div>
        </Section>
      )}
    </AppShell>
  );
}
